// /var/www/devit-dashboard/src/pages/Leads.js
import { useState, useEffect } from 'react';
import { leads as api } from '../services/api';

const TIPOS  = ['', 'ALQUILER', 'COMPRA', 'VENTA', 'ARRENDAR'];
const CANALES = ['', 'web', 'whatsapp'];
const ESTADOS = ['nuevo', 'contactado', 'visita', 'cerrado', 'descartado'];

const ESTADO_COLOR = {
  nuevo:       { bg: '#1a3a5c', text: '#60b4ff' },
  contactado:  { bg: '#3a2a00', text: '#ffb347' },
  visita:      { bg: '#2a3a00', text: '#b3e040' },
  cerrado:     { bg: '#003a2a', text: '#00d4a0' },
  descartado:  { bg: '#2a2a2a', text: '#888' },
};

const TIPO_COLOR = {
  ALQUILER: '#9b7fe8',
  COMPRA:   '#60b4ff',
  VENTA:    '#ff6b6b',
  ARRENDAR: '#00d4a0',
};

function resumenDatos(tipo, datos) {
  if (!datos) return '—';
  const d = datos;
  if (tipo === 'ALQUILER') {
    const partes = [];
    if (d.ingresos)   partes.push(`Ingresos: ${d.ingresos}`);
    if (d.ocupantes)  partes.push(`${d.ocupantes} ocupante(s)`);
    if (d.mascotas !== undefined) partes.push(d.mascotas === 'si' ? 'Con mascotas' : 'Sin mascotas');
    return partes.join(' · ') || '—';
  }
  if (tipo === 'COMPRA') {
    const partes = [];
    if (d.presupuesto)  partes.push(`Presupuesto: ${d.presupuesto}`);
    if (d.habitaciones) partes.push(`${d.habitaciones} hab.`);
    if (d.financiacion) partes.push(`Financiación: ${d.financiacion}`);
    return partes.join(' · ') || '—';
  }
  if (tipo === 'VENTA') {
    const partes = [];
    if (d.tipo)         partes.push(d.tipo);
    if (d.zona)         partes.push(d.zona);
    if (d.precio)       partes.push(d.precio);
    if (d.habitaciones) partes.push(`${d.habitaciones} hab.`);
    return partes.join(' · ') || '—';
  }
  if (tipo === 'ARRENDAR') {
    const partes = [];
    if (d.tipo)  partes.push(d.tipo);
    if (d.zona)  partes.push(d.zona);
    if (d.precio) partes.push(d.precio);
    return partes.join(' · ') || '—';
  }
  return '—';
}

function formatFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export default function Leads() {
  const [lista,    setLista]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filters,  setFilters]  = useState({ tipo_lead: '', canal: '', estado: '' });
  const [updating, setUpdating] = useState(null); // id del lead que se está actualizando

  const cargar = (f = filters) => {
    setLoading(true);
    api.getAll(f)
      .then(data => setLista(Array.isArray(data) ? data : []))
      .catch(() => setLista([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const handleFilter = (campo, valor) => {
    const nuevo = { ...filters, [campo]: valor };
    setFilters(nuevo);
    cargar(nuevo);
  };

  const handleEstado = async (lead, nuevoEstado) => {
    setUpdating(lead.id);
    try {
      await api.update(lead.id, { estado: nuevoEstado });
      setLista(prev => prev.map(l => l.id === lead.id ? { ...l, estado: nuevoEstado } : l));
    } finally {
      setUpdating(null);
    }
  };

  // ── Contadores por tipo ─────────────────────────────────────────────────
  const contadores = TIPOS.slice(1).reduce((acc, t) => {
    acc[t] = lista.filter(l => l.tipo_lead === t).length;
    return acc;
  }, {});

  return (
    <div className="page" style={{ padding: '32px', maxWidth: '1000px' }}>

      {/* ── Cabecera ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Leads</h1>
        <p style={{ color: 'var(--text2)', marginTop: '6px', fontSize: '14px', fontWeight: 400 }}>
          Contactos cualificados por el agente. Actualiza el estado según el seguimiento.
        </p>
      </div>

      {/* ── Badges resumen ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {TIPOS.slice(1).map(t => (
          <div key={t} style={{
            padding: '6px 14px', borderRadius: '20px',
            background: 'var(--bg2)', border: '1px solid var(--border)',
            fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: TIPO_COLOR[t], display: 'inline-block' }} />
            <span style={{ color: 'var(--text2)' }}>{t}</span>
            <span style={{ fontWeight: 700, color: 'var(--text1)' }}>{contadores[t]}</span>
          </div>
        ))}
        <div style={{
          padding: '6px 14px', borderRadius: '20px',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          fontSize: '13px', color: 'var(--text2)', marginLeft: 'auto'
        }}>
          Total: <strong style={{ color: 'var(--text1)' }}>{lista.length}</strong>
        </div>
      </div>

      {/* ── Filtros ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { campo: 'tipo_lead', label: 'Tipo', opciones: TIPOS,   labels: ['Todos los tipos', ...TIPOS.slice(1)] },
          { campo: 'canal',     label: 'Canal', opciones: CANALES, labels: ['Todos los canales', 'Web', 'WhatsApp'] },
          { campo: 'estado',    label: 'Estado', opciones: ['', ...ESTADOS], labels: ['Todos los estados', ...ESTADOS] },
        ].map(({ campo, opciones, labels }) => (
          <select
            key={campo}
            value={filters[campo]}
            onChange={e => handleFilter(campo, e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '8px',
              border: '1px solid var(--border)', background: 'var(--bg2)',
              color: 'var(--text1)', fontSize: '13px', cursor: 'pointer'
            }}
          >
            {opciones.map((op, i) => (
              <option key={op} value={op}>{labels[i]}</option>
            ))}
          </select>
        ))}

        <button
          onClick={() => { setFilters({ tipo_lead: '', canal: '', estado: '' }); cargar({ tipo_lead: '', canal: '', estado: '' }); }}
          style={{
            padding: '8px 14px', borderRadius: '8px',
            border: '1px solid var(--border)', background: 'var(--bg2)',
            color: 'var(--text2)', fontSize: '13px', cursor: 'pointer'
          }}
        >
          Limpiar
        </button>
      </div>

      {/* ── Tabla ────────────────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: 'var(--text2)' }}>Cargando leads...</p>
      ) : lista.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: '12px', padding: '48px', textAlign: 'center',
          color: 'var(--text2)', fontSize: '14px'
        }}>
          No hay leads con los filtros seleccionados.
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {/* cabecera tabla */}
          <div style={{
            display: 'grid', gridTemplateColumns: '120px 80px 1fr 160px 130px',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: '12px', color: 'var(--text2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>
            <span>Tipo</span>
            <span>Canal</span>
            <span>Resumen</span>
            <span>Fecha</span>
            <span>Estado</span>
          </div>

          {lista.map((lead, i) => {
            const ec = ESTADO_COLOR[lead.estado] || ESTADO_COLOR.nuevo;
            return (
              <div
                key={lead.id}
                style={{
                  display: 'grid', gridTemplateColumns: '120px 80px 1fr 160px 130px',
                  padding: '12px 16px', alignItems: 'center',
                  borderBottom: i < lista.length - 1 ? '1px solid var(--border)' : 'none',
                  fontSize: '13px',
                }}
              >
                {/* Tipo */}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  fontWeight: 600, color: TIPO_COLOR[lead.tipo_lead] || 'var(--text1)'
                }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: TIPO_COLOR[lead.tipo_lead], flexShrink: 0 }} />
                  {lead.tipo_lead}
                </span>

                {/* Canal */}
                <span style={{ color: 'var(--text2)', fontSize: '12px' }}>
                  {lead.canal === 'whatsapp' ? '📱 WA' : '🌐 Web'}
                </span>

                {/* Resumen datos */}
                <span style={{ color: 'var(--text2)', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {resumenDatos(lead.tipo_lead, lead.datos)}
                </span>

                {/* Fecha */}
                <span style={{ color: 'var(--text2)', fontSize: '12px' }}>
                  {formatFecha(lead.created_at)}
                </span>

                {/* Estado — select */}
                <select
                  value={lead.estado}
                  disabled={updating === lead.id}
                  onChange={e => handleEstado(lead, e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                    border: 'none', cursor: 'pointer',
                    background: ec.bg, color: ec.text,
                    opacity: updating === lead.id ? 0.5 : 1,
                  }}
                >
                  {ESTADOS.map(e => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
